import { memo, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { Button, Chip, Grid, Typography } from 'components/ui';
import { FormattedMessage } from 'react-intl';
import { TagGrid } from 'components/styles_components/container';
import Date from 'components/date';
import PlaceLabelQuery from 'queries/place/show_label';
import UserLabelQuery from 'queries/space/user_label';

const PlaceLabel = ({ id }) => {
  const { data } = useQuery(PlaceLabelQuery, { variables: { id }, fetchPolicy: 'cache-first' });
  return data?.place?.name ?? '';
};

const UserLabel = ({ id }) => {
  if (id === 0) {
    return <FormattedMessage id="autocomplete.user.not_assigned" />;
  }
  return <UserLabelLoader id={id} />;
};

const UserLabelLoader = ({ id }) => {
  const { data } = useQuery(UserLabelQuery, {
    variables: { userId: id },
    fetchPolicy: 'cache-first',
  });
  return data?.spaceUser?.email ?? '';
};

const INTL_KEYS = {
  name: 'name',
  firstname: 'user.firstname',
  surname: 'user.surname',
  email: 'email',
  brand: 'inspection_subject.brand',
  model: 'inspection_subject.model',
  serialNumber: 'inspection_subject.serial_number',
  status: 'inspection.status',
  dueDateFrom: 'filter.due_date_from',
  dueDateTo: 'filter.due_date_to',
  doneAtFrom: 'filter.done_at_from',
  doneAtTo: 'filter.done_at_to',
  canceledAtFrom: 'filter.canceled_at_from',
  canceledAtTo: 'filter.canceled_at_to',
  inspectorId: 'filter.inspector',
  placeId: 'filter.place',
  role: 'place.role',
  bucket: 'filter.bucket',
};

const VALUE_PARSERS = {
  name: (value) => value,
  firstname: (value) => value,
  surname: (value) => value,
  role: (value) => <FormattedMessage id={`place.role.${value}`} />,
  email: (value) => value,
  brand: (value) => value,
  model: (value) => value,
  serialNumber: (value) => value,
  status: (value) => <FormattedMessage id={`inspection.status.${value}`} />,
  dueDateFrom: (value) => <Date date={value} />,
  dueDateTo: (value) => <Date date={value} />,
  doneAtFrom: (value) => <Date date={value} />,
  doneAtTo: (value) => <Date date={value} />,
  canceledAtFrom: (value) => <Date date={value} />,
  canceledAtTo: (value) => <Date date={value} />,
  inspectorId: (id) => <UserLabel id={id} />,
  placeId: (id) => <PlaceLabel id={id} />,
  bucket: (value) => <FormattedMessage id={`filter.bucket.${value}`} />,
};

const FilterTags = ({ defaultValues, filterValues, onFilterChange }) => {

  const deleteKey = useCallback(
    (key) => {
      const newFilterValues = { ...filterValues };
      newFilterValues[key] = defaultValues[key];
      onFilterChange(newFilterValues);
    },
    [filterValues, defaultValues, onFilterChange]
  );

  const hasActiveFilters = Object.keys(filterValues).some(
    (key) => filterValues[key] !== defaultValues[key]
  );

  return (
    <TagGrid container spacing={2}>
      {hasActiveFilters && (
        <Grid>
          <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
            <FormattedMessage id="filter.active_filters" />
          </Typography>
        </Grid>
      )}
      {Object.keys(filterValues).map((key) => {
        if (filterValues[key] === defaultValues[key]) return null;
        if (!INTL_KEYS[key] || !VALUE_PARSERS[key]) return null;
        return (
          <Grid key={key}>
            <Chip
              color="primary"
              variant="outlined"
              label={
                <>
                  <FormattedMessage id={INTL_KEYS[key]} />{' '}
                  <b>{VALUE_PARSERS[key](filterValues[key])}</b>
                </>
              }
              onDelete={() => deleteKey(key)}
            />
          </Grid>
        );
      })}
      {hasActiveFilters && (
        <Grid>
          <Button
            size="small"
            color="secondary"
            variant="text"
            onClick={() => onFilterChange(defaultValues)}
          >
            <FormattedMessage id="button.clear_all" />
          </Button>
        </Grid>
      )}
    </TagGrid>
  );
};

export default memo(FilterTags);
